// FMO Dashboard - KPIs, agency overview, alerts
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Users, AlertTriangle, TrendingUp, DollarSign,
  RefreshCw, ChevronRight, Shield, Activity, Bell
} from 'lucide-react';
import { TourBubble } from '../../components/tour/TourOverlay';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const FMO_DASH_TOUR_STEPS = [
  { type: 'splash', title: 'FMO Command Center', description: 'Welcome to your FMO dashboard — a bird\'s-eye view of every agency, agent, and client in your network. Monitor retention, risk, revenue, and compliance across your entire organization.' },
  { target: 'fmo-kpis', emoji: '📊', title: 'Key Metrics', description: 'Six KPIs at a glance: total agencies, agents, clients, average retention, at-risk clients, and revenue at risk. These update in real-time from all connected agencies.', position: 'bottom' },
  { target: 'fmo-agencies', emoji: '🏢', title: 'Agency Portfolio', description: 'Your agencies listed by size. Click any agency to drill into their agents, clients, and risk details. Red badges show agencies with at-risk clients needing attention.', position: 'top' },
  { target: 'fmo-alerts', emoji: '🔔', title: 'Alerts', description: 'Critical notifications from across your network — compliance expirations, risk spikes, retention drops, and agency activity changes. Address red alerts first.', position: 'left' },
  { target: 'fmo-quicklinks', emoji: '⚡', title: 'Quick Links', description: 'Jump directly to Retention Analytics, Risk Overview, Revenue, or Compliance. Each gives you cross-agency insights that no individual agency can see.', position: 'top' },
  { type: 'celebration', title: 'You\'re in Control!', description: 'FMOs using Pro IRP see an average 5% retention improvement across their agencies — that\'s $225K saved for every 30K clients. Check in daily for the latest insights.' },
];

function KpiCard({ title, value, subtitle, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 border-t-4" style={{ borderTopColor: color || '#FFB800' }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-500 font-medium">{title}</span>
        {Icon && <Icon size={20} className="text-gray-400" />}
      </div>
      <p className="text-3xl font-bold text-[#172A3A]">{value}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );
}

export default function FmoDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [fmoDashTourStep, setFmoDashTourStep] = useState(0);
  const [showFmoDashTour, setShowFmoDashTour] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('fmo-dashboard-tour-dismissed')) setShowFmoDashTour(true);
  }, []);

  const advanceFmoDashTour = useCallback(() => {
    setFmoDashTourStep(s => {
      if (s + 1 >= FMO_DASH_TOUR_STEPS.length) { localStorage.setItem('fmo-dashboard-tour-dismissed', 'true'); setShowFmoDashTour(false); return 0; }
      return s + 1;
    });
  }, []);
  const backFmoDashTour = useCallback(() => setFmoDashTourStep(s => Math.max(0, s - 1)), []);
  const dismissFmoDashTour = useCallback(() => { localStorage.setItem('fmo-dashboard-tour-dismissed', 'true'); setShowFmoDashTour(false); }, []);

  useEffect(() => {
    if (!showFmoDashTour) return;
    const h = (e) => { if (e.key === 'Escape') dismissFmoDashTour(); if (e.key === 'ArrowRight' || e.key === 'Enter') advanceFmoDashTour(); if (e.key === 'ArrowLeft') backFmoDashTour(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [showFmoDashTour, advanceFmoDashTour, backFmoDashTour, dismissFmoDashTour]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/fmo/dashboard`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json);
    } catch (err) {
      console.error('[FMO Dashboard] Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const agencies = data?.agencyOverview || [];
  const alerts = data?.alerts || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#172A3A]">FMO Command Center</h1>
            <p className="text-sm text-gray-500">Cross-agency performance and retention intelligence</p>
          </div>
          <button onClick={fetchDashboard} className="p-2 hover:bg-gray-100 rounded-lg">
            <RefreshCw size={18} className="text-gray-500" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-tour="fmo-kpis">
          <KpiCard title="Agencies" value={kpis.totalAgencies} icon={Building2} color="#3b82f6" />
          <KpiCard title="Agents" value={kpis.totalAgents} icon={Users} color="#8b5cf6" />
          <KpiCard title="Total Clients" value={kpis.totalClients?.toLocaleString()} icon={Users} color="#FFB800" />
          <KpiCard title="Retention" value={`${kpis.avgRetention}%`} icon={TrendingUp} color="#22c55e" />
          <KpiCard title="At Risk" value={kpis.atRiskClients} icon={AlertTriangle} color="#ef4444" />
          <KpiCard title="Revenue at Risk" value={`$${((kpis.revenueAtRisk || 0) / 1000).toFixed(0)}K`} icon={DollarSign} color="#f97316" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Agency Overview */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200" data-tour="fmo-agencies">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Building2 size={18} className="text-gray-400" />
                Agency Portfolio
              </h3>
              <Link to="/fmo/agencies" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                View All <ChevronRight size={14} />
              </Link>
            </div>
            {agencies.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Building2 size={40} className="mx-auto mb-2 opacity-30" />
                <p>No agencies linked yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {agencies.slice(0, 10).map(a => (
                  <Link key={a.agencyId} to={`/fmo/agencies/${a.agencyId}`}
                    className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div>
                      <p className="font-semibold text-gray-900">{a.agencyName}</p>
                      <p className="text-xs text-gray-400">{a.agentCount} agents &middot; {a.clientCount} clients</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {a.atRisk > 0 && (
                        <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full">
                          <AlertTriangle size={12} /> {a.atRisk} at risk
                        </span>
                      )}
                      <ChevronRight size={16} className="text-gray-300" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Alerts */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200" data-tour="fmo-alerts">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Bell size={18} className="text-gray-400" />
                Alerts
                {alerts.length > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{alerts.length}</span>
                )}
              </h3>
            </div>
            {alerts.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                No new alerts
              </div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                {alerts.map(alert => (
                  <div key={alert.id} className="px-6 py-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={14} className={
                        alert.severity === 'critical' ? 'text-red-500' :
                        alert.severity === 'warning' ? 'text-yellow-500' : 'text-blue-500'
                      } />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{alert.title}</p>
                        <p className="text-xs text-gray-400">{alert.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-tour="fmo-quicklinks">
          {[
            { to: '/fmo/analytics', label: 'Retention Analytics', icon: TrendingUp, color: 'bg-green-50 text-green-600' },
            { to: '/fmo/risk', label: 'Risk Overview', icon: Shield, color: 'bg-red-50 text-red-600' },
            { to: '/fmo/revenue', label: 'Revenue', icon: DollarSign, color: 'bg-amber-50 text-amber-600' },
            { to: '/fmo/compliance', label: 'Compliance', icon: Shield, color: 'bg-blue-50 text-blue-600' },
          ].map(link => (
            <Link key={link.to} to={link.to}
              className={`flex items-center gap-3 p-4 rounded-xl border border-gray-200 bg-white hover:shadow-md transition-shadow`}>
              <div className={`p-2 rounded-lg ${link.color}`}>
                <link.icon size={20} />
              </div>
              <span className="font-medium text-gray-900 text-sm">{link.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {showFmoDashTour && (
        <TourBubble stepData={FMO_DASH_TOUR_STEPS[fmoDashTourStep]} stepIndex={fmoDashTourStep} totalSteps={FMO_DASH_TOUR_STEPS.length}
          onNext={advanceFmoDashTour} onBack={backFmoDashTour} onDismiss={dismissFmoDashTour}
          splashIcon={<Building2 className="w-10 h-10 text-amber-500" />} maskId="fmo-dashboard-tour-mask" />
      )}
    </div>
  );
}
